/**
 * The credit-spread workbench: a chain to pick a spread from, the underlying's daily
 * chart, and a backtest of the picked spread replayed over history.
 *
 * Prices here are `number`, not `Decimal`, and that is not the ledger's rule relaxed.
 * Nothing on this page is booked: every figure is an estimate derived from market data
 * that arrives as doubles, and rounding a double into a `Decimal` would claim an
 * exactness the source never had.
 */

export type OptionRight = 'call' | 'put';

export const OPTION_RIGHTS: ReadonlyArray<OptionRight> = ['call', 'put'];

// ---- the chain ----

export interface GetCreditSpreadChainRequest {
  readonly symbol: string;
  readonly right: OptionRight;
}

/** The price every moneyness on the page is measured against. */
export interface UnderlyingPrice {
  readonly symbol: string;
  /**
   * The last regular-session trade. Option quotes freeze at the close, so pricing them
   * against an after-hours print would move every moneyness while no option moved.
   */
  readonly price: number;
  readonly pricedAt: number;
}

/** One out-of-the-money contract, with what it is worth relative to its neighbour. */
export interface CreditSpreadChainRow {
  /** OCC contract symbol. */
  readonly symbol: string;
  /** `YYYY-MM-DD`. */
  readonly expiration: string;
  /** Calendar days from today (Eastern) to expiration. */
  readonly dte: number;
  readonly strike: number;
  /** Distance out of the money as a fraction of the underlying: `(K − S)/S` for a call, `(S − K)/S` for a put. */
  readonly moneyness: number;
  readonly bid?: number;
  readonly ask?: number;
  /** The quote midpoint, absent when either side is missing. */
  readonly mid?: number;
  /** `(ask − bid)/mid`: the round trip as a share of what the contract is worth. */
  readonly spreadPct?: number;
  readonly last?: number;
  readonly lastAt?: number;
  readonly delta?: number;
  readonly iv?: number;
  /** The next strike further out of the money in the same expiration: higher for a call, lower for a put. */
  readonly neighborStrike?: number;
  /** This contract's mid minus the neighbour's — the credit a spread against it would collect at mid. */
  readonly neighborPriceDiff?: number;
  /** `neighborPriceDiff / |strike − neighborStrike|`: credit per dollar of width, which is also the loss the spread risks per dollar it collects. */
  readonly neighborRelativeDiff?: number;
}

export interface GetCreditSpreadChainResponse {
  readonly underlying: UnderlyingPrice;
  readonly right: OptionRight;
  /** Expirations 7 to 45 days out that have at least one out-of-the-money contract, ascending. */
  readonly expirations: ReadonlyArray<string>;
  /** Sorted by expiration, then by distance from the money. */
  readonly rows: ReadonlyArray<CreditSpreadChainRow>;
}

// ---- the chart ----

export interface ListDailyBarsRequest {
  readonly symbol: string;
  /** Inclusive, `YYYY-MM-DD`. */
  readonly from: string;
  /** Inclusive, `YYYY-MM-DD`. */
  readonly to: string;
}

/** Split-adjusted, so a chart spanning a split is one continuous line. */
export interface DailyBar {
  readonly date: string;
  readonly open: number;
  readonly high: number;
  readonly low: number;
  readonly close: number;
  readonly volume: number;
}

export interface ListDailyBarsResponse {
  readonly bars: ReadonlyArray<DailyBar>;
}

// ---- earnings ----

export interface ListEarningsRequest {
  readonly symbol: string;
}

/** `unknown` is a symbol the calendar has no record of — not one that reports nothing. */
export type EarningsCoverage = 'reporting' | 'non-reporting' | 'unknown';

export const EARNINGS_COVERAGES: ReadonlyArray<EarningsCoverage> = ['reporting', 'non-reporting', 'unknown'];

export type EarningsTiming = 'before-open' | 'after-close';

export const EARNINGS_TIMINGS: ReadonlyArray<EarningsTiming> = ['before-open', 'after-close'];

export interface EarningsReportView {
  /** `YYYY-MM-DD`, Eastern, the day the results were released. */
  readonly date: string;
  readonly timing: EarningsTiming;
  /** As the company names it: `Q3 FY2026`. */
  readonly fiscalPeriod: string;
  /** The session whose open the report moves: the next one after the close, the same one before the open. */
  readonly reactionDate: string;
}

export interface ListEarningsResponse {
  readonly symbol: string;
  readonly coverage: EarningsCoverage;
  /** The stretch every report is recorded over. A report after `completeThrough` may not be listed yet. */
  readonly completeFrom: string;
  readonly completeThrough: string;
  /** Oldest first. */
  readonly reports: ReadonlyArray<EarningsReportView>;
}

// ---- the backtest ----

export interface StartCreditSpreadBacktestRequest {
  /** OCC symbol of the leg sold: the one nearer the money. */
  readonly shortSymbol: string;
  /** OCC symbol of the leg bought, in the same expiration and further out of the money. */
  readonly longSymbol: string;
  /** Close once this fraction of the credit is kept: 0.6 buys back at 40% of the credit. */
  readonly takeProfit: number;
  /** Close once the loss reaches this multiple of the credit: 2 buys back at 3× the credit. */
  readonly stopLoss: number;
  /** Inclusive range of calendar days to expiration a historical entry may take. */
  readonly dteMin: number;
  readonly dteMax: number;
  /** Inclusive range of short-leg moneyness a historical entry may take, as fractions. */
  readonly moneynessMin: number;
  readonly moneynessMax: number;
  /** `HH:mm` Eastern on a regular session. */
  readonly entryTime: string;
  /** `HH:mm` Eastern on a session that closes early. */
  readonly shortDayEntryTime: string;
  /** First entry date, inclusive, `YYYY-MM-DD`. */
  readonly from: string;
  /** Last entry date, inclusive, `YYYY-MM-DD`. */
  readonly to: string;
  /**
   * Skip an entry whose time to expiration covers an earnings reaction. Refused for a symbol
   * the earnings calendar does not cover, since "no report found" would be a guess.
   */
  readonly avoidEarnings: boolean;
}

export interface StartCreditSpreadBacktestResponse {
  readonly backtestId: string;
}

export interface GetCreditSpreadBacktestRequest {
  readonly backtestId: string;
}

export type BacktestStatus = 'running' | 'succeeded' | 'failed';

export const BACKTEST_STATUSES: ReadonlyArray<BacktestStatus> = ['running', 'succeeded', 'failed'];

/** `open` is a position whose expiration lies past the data: it has no outcome yet. */
export type BacktestExitReason = 'take_profit' | 'stop_loss' | 'expired' | 'open';

export const BACKTEST_EXIT_REASONS: ReadonlyArray<BacktestExitReason> = ['take_profit', 'stop_loss', 'expired', 'open'];

/** What the picked spread looked like today, which is what every historical entry imitates. */
export interface BacktestTemplate {
  readonly underlying: string;
  readonly right: OptionRight;
  readonly expiration: string;
  readonly dte: number;
  readonly shortStrike: number;
  readonly longStrike: number;
  readonly underlyingPrice: number;
  readonly shortMoneyness: number;
  /** Strike distance as a fraction of the underlying. Historical entries keep this, not the dollar width. */
  readonly widthPct: number;
  /** `(ask − bid)/mid` of each leg today, applied to the historical prints to estimate an entry fill. */
  readonly shortSpreadPct: number;
  readonly longSpreadPct: number;
  /** What the spread collects today selling the short at its bid and buying the long at its ask. */
  readonly naturalCredit: number;
}

export interface BacktestPosition {
  readonly entryDate: string;
  readonly entryAt: number;
  readonly expiration: string;
  readonly dte: number;
  readonly shortSymbol: string;
  readonly longSymbol: string;
  readonly shortStrike: number;
  readonly longStrike: number;
  readonly underlyingPrice: number;
  readonly shortMoneyness: number;
  /** Per share, after crossing each leg's estimated spread. */
  readonly credit: number;
  /**
   * The capital the position ties up, per share: width less credit, which is what it loses
   * if both legs finish in the money. What its returns are measured against.
   */
  readonly maxLoss: number;
  readonly exitReason: BacktestExitReason;
  /** Absent while `open`. */
  readonly exitAt?: number;
  readonly exitDate?: string;
  /** What buying the spread back cost, per share. Absent while `open`. */
  readonly exitDebit?: number;
  /** `credit − exitDebit`, per share. Absent while `open`. */
  readonly profit?: number;
  /** Calendar days from entry to exit. Absent while `open`. */
  readonly daysHeld?: number;
  /** `profit / maxLoss`. Absent while `open`. */
  readonly returnOnCapital?: number;
  /**
   * `returnOnCapital × 365 / daysHeld`: simple, not compounded, since compounding a
   * one-day hold turns a good day into a number nobody could earn. Absent while `open`.
   */
  readonly annualizedReturn?: number;
}

/** Entries that were not taken, and why — so a thin result says what thinned it. */
export interface BacktestSkips {
  /** Sessions on which no listed spread matched the ranges, or the underlying had no price at the entry time. */
  readonly noCandidates: number;
  /** Candidate spreads a leg of which had not traded in the 30 minutes before the entry time. */
  readonly noPrints: number;
  /** Candidate spreads whose legs traded, but crossing their spreads left no credit. */
  readonly noCredit: number;
  /** With `avoidEarnings`: candidate spreads an earnings reaction fell inside the life of. */
  readonly earnings: number;
  /** With `avoidEarnings`: candidate spreads expiring past where the earnings calendar is complete, so it could not say. */
  readonly earningsUnknown: number;
  /**
   * Candidate spreads whose estimated credit was at least their width — no capital at risk,
   * which only prints from different moments can produce. Not a trade anyone was offered.
   */
  readonly creditAboveWidth: number;
}

export interface BacktestStats {
  readonly positions: number;
  readonly closed: number;
  readonly takeProfits: number;
  readonly stopLosses: number;
  readonly expired: number;
  readonly open: number;
  /** Closed positions with a positive profit, over closed positions. */
  readonly winRate?: number;
  readonly averageDaysHeld?: number;
  readonly medianDaysHeld?: number;
  /** Per share, over closed positions. */
  readonly averageProfit?: number;
  readonly totalProfit: number;
  /** Profit over credit, averaged over closed positions. */
  readonly averageReturnOnCredit?: number;
  /** Profit over capital (max loss), averaged over closed positions. */
  readonly averageReturnOnCapital?: number;
  /**
   * What capital earned per year while tied up: `Σ profit / Σ (maxLoss × daysHeld) × 365`
   * over closed positions. Weighted by capital and time, so a handful of one-day trades
   * cannot dominate it as they would an average of each position's annualized return.
   */
  readonly annualizedReturnOnCapital?: number;
}

export interface BacktestResult {
  readonly template: BacktestTemplate;
  readonly positions: ReadonlyArray<BacktestPosition>;
  readonly skips: BacktestSkips;
  readonly stats: BacktestStats;
}

export interface BacktestProgress {
  readonly done: number;
  readonly total: number;
  readonly stage: string;
}

export interface CreditSpreadBacktest {
  readonly backtestId: string;
  readonly status: BacktestStatus;
  readonly startedAt: number;
  readonly finishedAt?: number;
  readonly request: StartCreditSpreadBacktestRequest;
  readonly progress: BacktestProgress;
  /** Set when `failed`. */
  readonly error?: string;
  /** Set when `succeeded`. */
  readonly result?: BacktestResult;
}

export interface GetCreditSpreadBacktestResponse {
  readonly backtest: CreditSpreadBacktest;
}
